/**
 * Keyboard, mouse, and touch input handler — tracks mouse/touch position,
 * key state, click/tap events, and multi-touch for pinch zoom.
 *
 * Touch semantics:
 *   - Single tap (no drag) → clicked=true, clickScreenX/Y set
 *   - Single finger drag → mouseDeltaX/Y reported for camera pan
 *   - Two-finger pinch → wheelDelta reported for camera zoom
 */

export interface InputState {
  keys: Set<string>;
  keysJustPressed: Set<string>;
  mouseX: number;
  mouseY: number;
  mouseDown: boolean;
  mouseButton: number;
  mouseWorldX: number;
  mouseWorldY: number;
  mouseDeltaX: number;
  mouseDeltaY: number;
  wheelDelta: number;
  clicked: boolean;
  clickScreenX: number;
  clickScreenY: number;
  /** True during a single-finger drag (touch only, for camera pan). */
  touchDragging: boolean;
  /** True when 2+ fingers are on screen (for pinch-to-zoom). */
  touchPinching: boolean;
}

const TRACKED_KEYS = [
  'w', 'a', 's', 'd',
  'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
  'Shift', ' ', 'b', 'Escape', 'g', 'B', 'G', 't', 'T',
  '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '_',
  '/',
];

const TAP_MAX_MOVE = 10;   // px — movement below this is a tap, not a drag
const TAP_MAX_TIME = 300;  // ms

export class InputMouseKeyboardHandler {
  state: InputState = {
    keys: new Set(),
    keysJustPressed: new Set(),
    mouseX: 0,
    mouseY: 0,
    mouseDown: false,
    mouseButton: 0,
    mouseWorldX: 0,
    mouseWorldY: 0,
    mouseDeltaX: 0,
    mouseDeltaY: 0,
    wheelDelta: 0,
    clicked: false,
    clickScreenX: 0,
    clickScreenY: 0,
    touchDragging: false,
    touchPinching: false,
  };

  private canvas: HTMLCanvasElement;
  private prevKeys: Set<string> = new Set();

  // Touch tracking
  private touchStartTime = 0;
  private touchStartX = 0;
  private touchStartY = 0;
  private touchMoved = false;
  private activeTouches = 0;
  private lastPinchDist = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;

    // --- Keyboard ---
    window.addEventListener('keydown', (e) => {
      // If GM input is focused, let the browser handle the key normally
      const active = document.activeElement;
      if (active && (active as HTMLElement).id === 'gm-input') return;
      if (TRACKED_KEYS.includes(e.key)) {
        e.preventDefault();
        this.state.keys.add(e.key);
      }
    });
    window.addEventListener('keyup', (e) => {
      this.state.keys.delete(e.key);
    });

    // --- Mouse ---
    this.canvas.addEventListener('mousedown', (e) => {
      this.state.mouseDown = true;
      this.state.mouseButton = e.button;
    });
    this.canvas.addEventListener('mouseup', (e) => {
      this.state.mouseDown = false;
      if (e.button === 0) {
        const rect = this.canvas.getBoundingClientRect();
        this.state.clicked = true;
        this.state.clickScreenX = e.clientX - rect.left;
        this.state.clickScreenY = e.clientY - rect.top;
      }
    });
    this.canvas.addEventListener('mousemove', (e) => {
      const rect = this.canvas.getBoundingClientRect();
      const newX = e.clientX - rect.left;
      const newY = e.clientY - rect.top;
      this.state.mouseDeltaX = newX - this.state.mouseX;
      this.state.mouseDeltaY = newY - this.state.mouseY;
      this.state.mouseX = newX;
      this.state.mouseY = newY;
    });
    this.canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.state.wheelDelta += e.deltaY > 0 ? 1 : e.deltaY < 0 ? -1 : 0;
    });
    this.canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });

    // --- Touch ---
    this.canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.activeTouches = e.touches.length;
      this.state.mouseDown = true;

      if (e.touches.length === 1) {
        // Single finger: track for tap vs drag
        const t = e.touches[0];
        const rect = this.canvas.getBoundingClientRect();
        this.touchStartX = t.clientX - rect.left;
        this.touchStartY = t.clientY - rect.top;
        this.touchStartTime = performance.now();
        this.touchMoved = false;
        this.state.mouseX = this.touchStartX;
        this.state.mouseY = this.touchStartY;
        this.state.touchDragging = false;
      } else if (e.touches.length >= 2) {
        // Multi-touch: start pinch tracking
        this.state.touchPinching = true;
        this.lastPinchDist = this.getTouchDistance(e);
      }
    }, { passive: false });

    this.canvas.addEventListener('touchmove', (e) => {
      e.preventDefault();
      this.activeTouches = e.touches.length;

      if (e.touches.length === 1) {
        const t = e.touches[0];
        const rect = this.canvas.getBoundingClientRect();
        const nx = t.clientX - rect.left;
        const ny = t.clientY - rect.top;
        this.state.mouseDeltaX = nx - this.state.mouseX;
        this.state.mouseDeltaY = ny - this.state.mouseY;
        this.state.mouseX = nx;
        this.state.mouseY = ny;

        // Detect drag threshold
        const dx = nx - this.touchStartX;
        const dy = ny - this.touchStartY;
        if (Math.abs(dx) > TAP_MAX_MOVE || Math.abs(dy) > TAP_MAX_MOVE) {
          this.touchMoved = true;
          this.state.touchDragging = true;
        }
      } else if (e.touches.length >= 2) {
        // Pinch zoom: compute distance change → wheelDelta
        const dist = this.getTouchDistance(e);
        if (this.lastPinchDist > 0) {
          const delta = this.lastPinchDist - dist; // positive = pinch in (zoom out)
          this.state.wheelDelta += delta * 0.05;
        }
        this.lastPinchDist = dist;
      }
    }, { passive: false });

    this.canvas.addEventListener('touchend', (e) => {
      e.preventDefault();
      this.state.mouseDown = false;
      this.activeTouches = e.touches.length;
      this.state.touchDragging = false;
      this.state.touchPinching = false;
      this.lastPinchDist = 0;

      if (!this.touchMoved && e.touches.length === 0) {
        // Tap completed
        const elapsed = performance.now() - this.touchStartTime;
        if (elapsed < TAP_MAX_TIME) {
          this.state.clicked = true;
          this.state.clickScreenX = this.state.mouseX;
          this.state.clickScreenY = this.state.mouseY;
        }
      }
    });

    this.canvas.addEventListener('touchcancel', () => {
      this.state.mouseDown = false;
      this.state.touchDragging = false;
      this.state.touchPinching = false;
      this.activeTouches = 0;
      this.lastPinchDist = 0;
    });
  }

  private getTouchDistance(e: TouchEvent): number {
    if (e.touches.length < 2) return 0;
    const t0 = e.touches[0];
    const t1 = e.touches[1];
    const dx = t1.clientX - t0.clientX;
    const dy = t1.clientY - t0.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  update(): void {
    // Compute keys just pressed this frame
    this.state.keysJustPressed.clear();
    for (const k of this.state.keys) {
      if (!this.prevKeys.has(k)) {
        this.state.keysJustPressed.add(k);
      }
    }
    this.prevKeys = new Set(this.state.keys);

    // Reset per-frame accumulators
    this.state.wheelDelta = 0;
    this.state.clicked = false;
    this.state.mouseDeltaX = 0;
    this.state.mouseDeltaY = 0;
  }
}
